import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  ParametresToit,
  StructureToit,
  GeometrieBaleine,
  TypeStyleBaleine,
  TypeMateriauToit,
  TypePointJonction,
  Point3D,
  Point2D,
  DonneesStructureBasePourToit,
  ProfilBaleinePersonnalise,
  CalculLongueurBaleine,
  StyleBaleinePredefini
} from '../types';

/**
 * Service de génération 3D du toit
 * Responsable de la création de la géométrie 3D des baleines selon les styles choisis
 * Conforme au cahier des charges - Module 4: Le Toit de la Tonnelle
 * 
 * GÉNÉRATION LOCALE OBLIGATOIRE:
 * - Élimination des clignotements
 * - Suppression des résidus fantômes
 * - Réactivité instantanée
 * - Expérience utilisateur fluide
 */
export class Toit3DService {
  private materiau: THREE.Material;
  private materiauJonction: THREE.MeshLambertMaterial;
  private derniereReference: any = null;
  private derniereStructure: StructureToit | null = null;
  private logBuffer: string[] = [];

  constructor() {
    this.materiau = new THREE.MeshLambertMaterial({ 
      color: 0x8B4513, // Couleur bois par défaut
      transparent: false
    });
    
    this.materiauJonction = new THREE.MeshLambertMaterial({ 
      color: 0x654321, // Couleur plus foncée pour la jonction
      transparent: false
    });
    
    // Suppression du vidage automatique des logs au démarrage
    // this.viderFichierLogs();
  }

  /**
   * Génère la structure complète du toit
   * Tâche 4.1: Génération des Nervures du Toit ("Baleines")
   * Tâche 4.2: Mécanisme de Génération du Toit
   */
  public genererToit(
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): StructureToit {
    // Génération locale uniquement - pas de logs excessifs pour éviter le lag
    
    // Vérifier si une régénération est nécessaire
    const referenceActuelle = {
      parametres: JSON.stringify(parametres),
      donneesBase: JSON.stringify(donneesBase)
    };
    
    if (this.derniereReference && 
        this.derniereReference.parametres === referenceActuelle.parametres &&
        this.derniereReference.donneesBase === referenceActuelle.donneesBase &&
        this.derniereStructure) {
      console.log('🔍 [TRACE] Réutilisation de la structure mise en cache');
      return this.derniereStructure;
    }
    
    console.log('🔍 [TRACE] Génération d\'une nouvelle structure');
    console.log('🏠 Génération toit avec style:', parametres.styleBaleine);
    
    // Debug des données de base pour les cercles
    if (donneesBase.forme.toLowerCase() === 'cercle') {
      console.log('🔍 [DEBUG CERCLE DONNEES] Forme:', donneesBase.forme);
      console.log('🔍 [DEBUG CERCLE DONNEES] Largeur:', donneesBase.largeur);
      console.log('🔍 [DEBUG CERCLE DONNEES] Longueur:', donneesBase.longueur);
      console.log('🔍 [DEBUG CERCLE DONNEES] Centre:', donneesBase.centre);
    }

    // Mettre à jour le matériau selon les propriétés de la structure de base
    const couleurStructure = donneesBase.couleur || '#8B4513'; // Couleur par défaut
    const couleurNumerique = typeof couleurStructure === 'string' 
      ? parseInt(couleurStructure.replace('#', ''), 16) 
      : couleurStructure;
    
    // Appliquer la couleur de la structure de base
    this.mettreAJourMateriau(couleurNumerique);
    this.materiauJonction.color.set(couleurNumerique);
    
    // Appliquer les propriétés de la structure de base
    
    // Calculer le point de convergence
    const pointConvergence = this.calculerPointConvergence(parametres, donneesBase);

    // Déterminer le nombre de baleines en fonction du paramètre d'alignement
    const nombreBaleines = parametres.alignerSurMontants
      ? donneesBase.nombreMontants
      : (parametres.nombreBaleines || 8); // Valeur par défaut indépendante

    // Générer les baleines avec le profil 2D
    const baleines = this.genererBaleines(
      nombreBaleines,
      pointConvergence,
      parametres,
      donneesBase
    );

    // Générer le point de jonction si nécessaire
    const pointJonction = this.genererPointJonction(pointConvergence, parametres, donneesBase);

    // Enrichir les paramètres avec les données de la structure de base
    // L'épaisseur du toit doit être préservée et non écrasée par celle de la base
    const parametresEnrichis = {
      ...parametres,
      materiau: donneesBase.materiau,
      couleur: donneesBase.couleur
      // L'épaisseur reste celle des paramètres du toit
    };
    
    // Paramètres enrichis du toit

    const structure: StructureToit = {
      id: 'structure_toit',
      parametres: parametresEnrichis,
      baleines,
      pointConvergence,
      pointJonction
    };

    // Stocker la structure pour pouvoir accéder au matériau plus tard
    this.derniereStructure = structure;
    this.derniereReference = referenceActuelle;

    return structure;
  }

  /**
   * Génère la géométrie 3D pour Three.js
   * Méthode appelée par le composant Visualisateur3D
   */
  public genererToit3D(structure: StructureToit): THREE.Group {
    // Génération toit 3D
    const groupe = new THREE.Group();
    groupe.name = 'toit';

    // Stocker temporairement la structure pour accès au matériau
    this.derniereStructure = structure;

    // Générer les baleines
    structure.baleines.forEach((baleine, index) => {
      // Traitement baleine
      if (baleine.points3D.length > 0 && baleine.indices.length > 0) {
        const mesh = this.creerMeshBaleine(baleine);
        if (mesh) {
          mesh.name = `baleine_${index}`;
          groupe.add(mesh);
          // Baleine ajoutée au groupe
      }
    } else {
      // Baleine ignorée - géométrie insuffisante
      }
    });

    // Générer le point de jonction
    // Vérification point de jonction
    if (structure.pointJonction) {
      
      const meshJonction = this.creerMeshJonction(structure.pointJonction);
      if (meshJonction) {
        meshJonction.name = 'jonction_toit';
        groupe.add(meshJonction);
        // Point de jonction ajouté avec succès
      }
    } else {
      // Aucun point de jonction à ajouter
    }

    // Groupe toit créé
    return groupe;
  }

  /**
   * Tâche 4.5: Adaptation Dynamique Cruciale de la Longueur des Baleines
   * Calcule la longueur optimale pour chaque baleine selon sa position
   */
  private calculerLongueursBaleines(
    nombreBaleines: number,
    pointConvergence: Point3D,
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): CalculLongueurBaleine[] {
    const calculs: CalculLongueurBaleine[] = [];
    
    // Déterminer les points d'attache sur la poutre haute ou double poutre
    const poutreReference = donneesBase.doublePoutreActive && donneesBase.doublePoutre 
      ? donneesBase.doublePoutre 
      : donneesBase.poutreHaute;

    const pointsAttache = this.calculerPointsAttache(
      nombreBaleines,
      poutreReference,
      donneesBase,
      !!parametres.alignerSurMontants
    );

    pointsAttache.forEach((pointAttache, index) => {
      const longueur = this.calculerDistance3D(pointAttache, pointConvergence);
      const angle = this.calculerAngle(pointAttache, donneesBase.centre);
      const distanceAuCentre = this.calculerDistance3D(pointAttache, donneesBase.centre);

      calculs.push({
        indexMontant: index,
        pointAttache,
        pointConvergence,
        longueur,
        angle,
        distanceAuCentre
      });
    });

    return calculs;
  }

  /**
   * Calcule les points d'attache des baleines sur la poutre
   * Tâche 4.1: Attachement - "collé" à la poutre haute
   * CORRECTION: Les baleines sont positionnées aux coins ET réparties uniformément
   * Le nombre de baleines est indépendant du nombre de montants
   * NOUVELLE CORRECTION: La hauteur des points d'attache suit proportionnellement la hauteur de convergence
   */
  private calculerPointsAttache(
    nombreBaleines: number,
    poutre: { points: Point3D[]; hauteur: number },
    donneesBase: DonneesStructureBasePourToit,
    alignerSurMontants: boolean
  ): Point3D[] {
    const pointsAttache: Point3D[] = [];
    
    if (alignerSurMontants) {
      // Aligner strictement chaque baleine au-dessus de chaque montant
      console.log(`📐 Alignement activé: ${donneesBase.nombreMontants} baleines calées sur ${donneesBase.nombreMontants} montants`);
      const hauteurAttache = poutre.hauteur;
      donneesBase.positionsMontants.forEach((pos, i) => {
        const p: Point3D = { x: pos.x, y: hauteurAttache, z: pos.z };
        pointsAttache.push(p);
        console.log(`📍 Baleine ${i} (montant): position=(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)})`);
      });
      return pointsAttache;
    }

    console.log(`🏗️ Calcul des points d'attache pour ${nombreBaleines} baleines (indépendant des ${donneesBase.nombreMontants} montants)`);
    
    // Les points d'attache restent TOUJOURS à la hauteur de la poutre
    const hauteurAttache = poutre.hauteur;
    
    console.log(`📏 Hauteur attache: ${hauteurAttache.toFixed(2)}m`);
    
    // NOUVELLE LOGIQUE: Toujours placer des baleines aux coins + répartition uniforme
    const { forme, largeur, longueur = largeur, centre } = donneesBase;
    
    // Calculer les positions des coins selon la forme
    const pointsCoins = this.calculerPointsCoins(forme, largeur, longueur, centre, hauteurAttache);
    
    if (nombreBaleines <= pointsCoins.length) {
      // Si moins de baleines que de coins, prendre les premiers coins
      for (let i = 0; i < nombreBaleines; i++) {
        pointsAttache.push(pointsCoins[i]);
        console.log(`📍 Baleine ${i} (coin): position=(${pointsCoins[i].x.toFixed(2)}, ${pointsCoins[i].y.toFixed(2)}, ${pointsCoins[i].z.toFixed(2)})`);
      }
    } else {
      // Ajouter tous les coins d'abord
      pointsAttache.push(...pointsCoins);
      console.log(`📍 ${pointsCoins.length} baleines placées aux coins`);
      
      // Puis répartir les baleines restantes uniformément sur le périmètre
      const baleinesRestantes = nombreBaleines - pointsCoins.length;
      const angleStep = (2 * Math.PI) / baleinesRestantes;
      
      for (let i = 0; i < baleinesRestantes; i++) {
        const angle = i * angleStep;
        const point = this.calculerPointSurPerimetre(angle, donneesBase, hauteurAttache);
        pointsAttache.push(point);
        console.log(`📍 Baleine ${pointsCoins.length + i} (périmètre): angle=${(angle * 180 / Math.PI).toFixed(1)}°, position=(${point.x.toFixed(2)}, ${point.y.toFixed(2)}, ${point.z.toFixed(2)})`);
      }
    }

    return pointsAttache;
  }

  /**
   * Calcule les positions des coins selon la forme géométrique
   */
  private calculerPointsCoins(
    forme: string,
    largeur: number,
    longueur: number,
    centre: Point3D,
    hauteur: number
  ): Point3D[] {
    const coins: Point3D[] = [];
    const demiLargeur = largeur / 2;
    const demiLongueur = longueur / 2;
    
    // Debug spécifique pour les cercles
    if (forme.toLowerCase() === 'cercle') {
      console.log(`🔍 [DEBUG CERCLE] Dimensions reçues: largeur=${largeur}, longueur=${longueur}`);
      console.log(`🔍 [DEBUG CERCLE] Demi-dimensions: demiLargeur=${demiLargeur}, demiLongueur=${demiLongueur}`);
    }
    
    switch (forme.toLowerCase()) {
      case 'carre':
      case 'rectangle':
        // 4 coins pour un rectangle/carré
        coins.push(
          { x: centre.x + demiLargeur, y: hauteur, z: centre.z + demiLongueur }, // Coin avant-droit
          { x: centre.x - demiLargeur, y: hauteur, z: centre.z + demiLongueur }, // Coin avant-gauche
          { x: centre.x - demiLargeur, y: hauteur, z: centre.z - demiLongueur }, // Coin arrière-gauche
          { x: centre.x + demiLargeur, y: hauteur, z: centre.z - demiLongueur }  // Coin arrière-droit
        );
        break;
        
      case 'cercle':
      case 'ovale':
        // 4 points cardinaux pour un cercle/ovale
        const rayonX = forme === 'cercle' ? largeur / 2 : demiLargeur;
        const rayonZ = forme === 'cercle' ? largeur / 2 : demiLongueur;
        coins.push(
          { x: centre.x + rayonX, y: hauteur, z: centre.z }, // Est
          { x: centre.x, y: hauteur, z: centre.z + rayonZ }, // Nord
          { x: centre.x - rayonX, y: hauteur, z: centre.z }, // Ouest
          { x: centre.x, y: hauteur, z: centre.z - rayonZ }  // Sud
        );
        break;
        
      default:
        // Pour les formes polygonales ou autres, approximation rectangulaire
        coins.push(
          { x: centre.x + demiLargeur, y: hauteur, z: centre.z + demiLongueur },
          { x: centre.x - demiLargeur, y: hauteur, z: centre.z + demiLongueur },
          { x: centre.x - demiLargeur, y: hauteur, z: centre.z - demiLongueur },
          { x: centre.x + demiLargeur, y: hauteur, z: centre.z - demiLongueur }
        );
    }
    
    console.log(`🔲 Forme ${forme}: ${coins.length} coins calculés`);
    return coins;
  }

  /**
   * Calcule des points intermédiaires entre les montants existants
   */
  private calculerPointsIntermediaires(
    positionsMontants: Point3D[],
    nombrePointsSupplementaires: number,
    hauteur: number
  ): Point3D[] {
    const pointsIntermediaires: Point3D[] = [];
    const nombreMontants = positionsMontants.length;
    
    // Calculer combien de points ajouter entre chaque paire de montants
    const pointsParSegment = Math.floor(nombrePointsSupplementaires / nombreMontants);
    let pointsRestants = nombrePointsSupplementaires % nombreMontants;
    
    for (let i = 0; i < nombreMontants; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % nombreMontants];
      
      // Nombre de points à ajouter sur ce segment
      let pointsSurSegment = pointsParSegment;
      if (pointsRestants > 0) {
        pointsSurSegment++;
        pointsRestants--;
      }
      
      // Ajouter les points intermédiaires sur ce segment
      for (let j = 1; j <= pointsSurSegment; j++) {
        const ratio = j / (pointsSurSegment + 1);
        const pointIntermediaire = {
          x: montantActuel.x + (montantSuivant.x - montantActuel.x) * ratio,
          y: hauteur,
          z: montantActuel.z + (montantSuivant.z - montantActuel.z) * ratio
        };
        pointsIntermediaires.push(pointIntermediaire);
      }
    }
    
    return pointsIntermediaires;
  }

  /**
   * Calcule un point sur le périmètre de la forme selon l'angle
   */
  private calculerPointSurPerimetre(
    angle: number,
    donneesBase: DonneesStructureBasePourToit,
    hauteur: number
  ): Point3D {
    const { forme, largeur, longueur = largeur, centre } = donneesBase;
    
    // Debug spécifique pour les cercles
    if (forme.toLowerCase() === 'cercle') {
      console.log(`🔍 [DEBUG CERCLE PERIMETRE] Dimensions: largeur=${largeur}, longueur=${longueur}`);
    }
    
    let x: number, z: number;
    
    switch (forme.toLowerCase()) {
      case 'cercle':
        const rayon = largeur / 2;
        console.log(`🔍 [DEBUG CERCLE PERIMETRE] Rayon calculé: ${rayon}`);
        x = centre.x + rayon * Math.cos(angle);
        z = centre.z + rayon * Math.sin(angle);
        break;
        
      case 'ovale':
        const rayonX = largeur / 2;
        const rayonZ = longueur / 2;
        x = centre.x + rayonX * Math.cos(angle);
        z = centre.z + rayonZ * Math.sin(angle);
        break;
        
      case 'carre':
      case 'rectangle':
        // Calculer le point sur le périmètre rectangulaire
        const demiLargeur = largeur / 2;
        const demiLongueur = longueur / 2;
        const pointRect = this.calculerPointSurRectangle(angle, demiLargeur, demiLongueur);
        x = centre.x + pointRect.x;
        z = centre.z + pointRect.y; // Point2D.y correspond à la coordonnée Z dans l'espace 3D
        break;
        
      default:
        // Forme polygonale ou autre - approximation circulaire
        const rayonApprox = largeur / 2;
        x = centre.x + rayonApprox * Math.cos(angle);
        z = centre.z + rayonApprox * Math.sin(angle);
    }
    
    return { x, y: hauteur, z };
  }

  /**
   * Calcule un point sur le périmètre d'un rectangle selon l'angle
   */
  private calculerPointSurRectangle(angle: number, demiLargeur: number, demiLongueur: number): Point2D {
    // Normaliser l'angle entre 0 et 2π
    const angleNorm = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    
    // Calculer les angles des coins
    const angleCorner1 = Math.atan2(demiLongueur, demiLargeur);
    const angleCorner2 = Math.PI - angleCorner1;
    const angleCorner3 = Math.PI + angleCorner1;
    const angleCorner4 = 2 * Math.PI - angleCorner1;
    
    let x: number, z: number;
    
    if (angleNorm <= angleCorner1 || angleNorm >= angleCorner4) {
      // Côté droit
      x = demiLargeur;
      z = demiLargeur * Math.tan(angleNorm);
    } else if (angleNorm <= angleCorner2) {
      // Côté haut
      z = demiLongueur;
      x = demiLongueur / Math.tan(angleNorm);
    } else if (angleNorm <= angleCorner3) {
      // Côté gauche
      x = -demiLargeur;
      z = -demiLargeur * Math.tan(angleNorm);
    } else {
      // Côté bas
      z = -demiLongueur;
      x = -demiLongueur / Math.tan(angleNorm);
    }
    
    return { x, y: z }; // y représente z dans le contexte 2D
  }

  /**
   * Calcule le point de convergence central
   * Tâche 4.1: Contrainte de Convergence
   */
  private calculerPointConvergence(
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): Point3D {
    const poutreReference = donneesBase.doublePoutreActive && donneesBase.doublePoutre 
      ? donneesBase.doublePoutre 
      : donneesBase.poutreHaute;
      
    // Rétablir le calcul: hauteur de la poutre + hauteur de convergence
    const hauteurTotale = poutreReference.hauteur + parametres.hauteurConvergence;
    
    return {
      x: donneesBase.centre.x,
      y: hauteurTotale,
      z: donneesBase.centre.z
    };
  }

  /**
   * Génère toutes les baleines du toit
   * Tâche 4.2: Mécanisme de Génération du Toit
   */
  private genererBaleines(
    nombreBaleines: number,
    pointConvergence: Point3D,
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): GeometrieBaleine[] {
    const baleines: GeometrieBaleine[] = [];
    
    // Calculer les longueurs optimales
    const calculsLongueur = this.calculerLongueursBaleines(nombreBaleines, pointConvergence, parametres, donneesBase);
    
    if (parametres.pointJonction === TypePointJonction.POUTRE_CIRCULAIRE) {
      const rayonJonction = parametres.rayonPointJonction || 0.1;
      const epaisseurTore = donneesBase.epaisseur ? donneesBase.epaisseur / 1000 : 0.02;
      const recul = rayonJonction + epaisseurTore / 2;
      calculsLongueur.forEach(calcul => {
        const dirToCenter = {
          x: calcul.pointConvergence.x - calcul.pointAttache.x,
          y: calcul.pointConvergence.y - calcul.pointAttache.y,
          z: calcul.pointConvergence.z - calcul.pointAttache.z
        };
        // Recul uniquement dans le plan horizontal XZ
        const distXZ = Math.sqrt(dirToCenter.x ** 2 + dirToCenter.z ** 2);
        if (distXZ > recul) {
          const normDirXZ = { x: dirToCenter.x / distXZ, z: dirToCenter.z / distXZ };
          const centreY = calcul.pointConvergence.y; // calage à la hauteur du tore
          calcul.pointConvergence = {
            x: calcul.pointConvergence.x - normDirXZ.x * recul,
            y: centreY,
            z: calcul.pointConvergence.z - normDirXZ.z * recul
          };
          // Recalculer la longueur avec cette nouvelle extrémité
          const dx = calcul.pointConvergence.x - calcul.pointAttache.x;
          const dy = calcul.pointConvergence.y - calcul.pointAttache.y;
          const dz = calcul.pointConvergence.z - calcul.pointAttache.z;
          calcul.longueur = Math.sqrt(dx * dx + dy * dy + dz * dz);
        }
      });
    }
    
    // Obtenir le profil 2D de la baleine
    const profil2D = this.obtenirProfil2DBaleine(parametres);
    
    // Code original décommenté - génération de toutes les baleines
    calculsLongueur.forEach((calcul, index) => {
      const epaisseurFinale = parametres.epaisseur || (donneesBase.epaisseur ? donneesBase.epaisseur / 1000 : 0.02);
      const baleine = this.genererBaleineIndividuelle(index, calcul, profil2D, parametres, epaisseurFinale);
      baleines.push(baleine);
    });
    
    // DEBUG: Code de débogage commenté
    // console.log('🔍 [DEBUG] Génération de 4 baleines pour débogage');
    // for (let i = 0; i < Math.min(4, calculsLongueur.length); i++) {
    //   const calcul = calculsLongueur[i];
    //   const epaisseurFinale = parametres.epaisseur || (donneesBase.epaisseur ? donneesBase.epaisseur / 1000 : 0.02);
    //   const baleine = this.genererBaleineIndividuelle(i, calcul, profil2D, parametres, epaisseurFinale);
    //   baleines.push(baleine);
    // }
    
    return baleines;
  }

  /**
   * Génère une baleine individuelle
   * Tâche 4.2: Duplication d'un profil 2D pivoté
   */
  private genererBaleineIndividuelle(
    index: number,
    calcul: CalculLongueurBaleine,
    profil2D: Point2D[],
    parametres: ParametresToit,
    epaisseur: number
  ): GeometrieBaleine {
    // Adapter le profil 2D à la longueur calculée
    const profilAdapte = this.adapterProfilALongueur(profil2D, calcul.longueur);
    
    // Convertir en points 3D et pivoter selon l'angle
    const points3D = this.convertirEtPivoterProfil(
      profilAdapte,
      calcul.pointAttache,
      calcul.pointConvergence,
      calcul.angle,
      epaisseur,
      parametres
    );
    
    // Générer les indices pour les triangles
    const indices = this.genererIndicesBaleine(points3D.length / 4); // Division par 4 car on a 4 points par section rectangulaire
    
    return {
      id: `baleine_${index}`,
      indexMontant: calcul.indexMontant,
      pointAttache: calcul.pointAttache,
      pointConvergence: calcul.pointConvergence,
      longueur: calcul.longueur,
      angle: calcul.angle,
      profil2D: profilAdapte,
      points3D,
      indices
    };
  }

  /**
   * Obtient le profil 2D de la baleine selon le style choisi
   * Tâche 4.3: Bibliothèque de Styles Prédéfinis
   * Tâche 4.4: Outil de Dessin Personnalisé
   */
  private obtenirProfil2DBaleine(parametres: ParametresToit): Point2D[] {
    if (parametres.styleBaleine === TypeStyleBaleine.PERSONNALISE && parametres.profilPersonnalise) {
      return this.convertirProfilPersonnalise(parametres.profilPersonnalise);
    } else {
      return this.obtenirProfilPredefini(parametres.styleBaleine);
    }
  }



  /**
   * Obtient un profil prédéfini selon le style
   * Tâche 4.3: Bibliothèque de Styles Prédéfinis
   * STYLES REDESSINÉS POUR DIFFÉRENCES VISUELLES MARQUÉES
   */
  private obtenirProfilPredefini(style: TypeStyleBaleine): Point2D[] {

    
    switch (style) {
      case TypeStyleBaleine.CLASSIQUE:
        // Style classique : profil rectangulaire simple et épais

        return [
          { x: 0, y: 0 },
          { x: 0.1, y: 0.4 },   // Montée rapide et haute
          { x: 0.9, y: 0.4 },   // Plateau épais
          { x: 1, y: 0 }
        ];
        
      case TypeStyleBaleine.COURBE_DOUCE:
        // Style courbe douce : arc harmonieux

        return [
          { x: 0, y: 0 },
          { x: 0.2, y: 0.25 },
          { x: 0.4, y: 0.35 },
          { x: 0.6, y: 0.35 },
          { x: 0.8, y: 0.25 },
          { x: 1, y: 0 }
        ];
        
      case TypeStyleBaleine.GOTHIQUE:
        // Style gothique : pic central très prononcé

        return [
          { x: 0, y: 0 },
          { x: 0.4, y: 0.1 },   // Montée progressive
          { x: 0.5, y: 0.6 },   // Pic central très haut
          { x: 0.6, y: 0.1 },   // Descente rapide
          { x: 1, y: 0 }
        ];
        
      case TypeStyleBaleine.MODERNE:
        // Style moderne : profil angulaire avec facettes
        return [
          { x: 0, y: 0 },
          { x: 0.15, y: 0.3 },  // Montée angulaire
          { x: 0.35, y: 0.15 }, // Descente
          { x: 0.65, y: 0.15 }, // Plateau bas
          { x: 0.85, y: 0.3 },  // Remontée
          { x: 1, y: 0 }
        ];
        
      case TypeStyleBaleine.ASIATIQUE:
        // Style asiatique : double courbe avec creux central

        return [
          { x: 0, y: 0 },
          { x: 0.2, y: 0.3 },   // Premier pic
          { x: 0.35, y: 0.1 },  // Creux
          { x: 0.5, y: 0.05 },  // Creux central profond
          { x: 0.65, y: 0.1 },  // Remontée
          { x: 0.8, y: 0.3 },   // Deuxième pic
          { x: 1, y: 0 }
        ];
        
      default:
        return this.obtenirProfilPredefini(TypeStyleBaleine.COURBE_DOUCE);
    }
  }

  /**
   * Convertit un profil personnalisé en points 2D
   */
  private convertirProfilPersonnalise(profil: ProfilBaleinePersonnalise): Point2D[] {
    const points: Point2D[] = [];
    
    // Convertir les lignes de crayon
    profil.lignes.forEach(ligne => {
      points.push(...ligne);
    });
    
    // Convertir les lignes droites
    profil.lignesDroites.forEach(ligne => {
      points.push(ligne.debut, ligne.fin);
    });
    
    // Convertir les courbes de Bézier (approximation par segments)
    profil.courbes.forEach(courbe => {
      const pointsCourbe = this.approximerCourbeBezier(courbe, 10);
      points.push(...pointsCourbe);
    });
    
    // Normaliser et trier les points
    return this.normaliserProfil(points);
  }

  /**
   * Approxime une courbe de Bézier par des segments de droite
   */
  private approximerCourbeBezier(courbe: any, nombreSegments: number): Point2D[] {
    const points: Point2D[] = [];
    
    for (let i = 0; i <= nombreSegments; i++) {
      const t = i / nombreSegments;
      const point = this.calculerPointBezier(t, courbe.debut, courbe.controle1, courbe.controle2, courbe.fin);
      points.push(point);
    }
    
    return points;
  }

  /**
   * Calcule un point sur une courbe de Bézier cubique
   */
  private calculerPointBezier(t: number, p0: Point2D, p1: Point2D, p2: Point2D, p3: Point2D): Point2D {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    return {
      x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
      y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y
    };
  }

  /**
   * Normalise un profil pour qu'il soit entre 0 et 1
   */
  private normaliserProfil(points: Point2D[]): Point2D[] {
    if (points.length === 0) return [];
    
    // Trouver les limites
    let minX = points[0].x, maxX = points[0].x;
    let minY = points[0].y, maxY = points[0].y;
    
    points.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });
    
    const largeur = maxX - minX;
    const hauteur = maxY - minY;
    
    if (largeur === 0 || hauteur === 0) return points;
    
    // Normaliser
    return points.map(point => ({
      x: (point.x - minX) / largeur,
      y: (point.y - minY) / hauteur
    }));
  }

  /**
   * Adapte un profil 2D à une longueur spécifique
   * Tâche 4.5: Adaptation Dynamique
   */
  private adapterProfilALongueur(profil: Point2D[], longueur: number): Point2D[] {
    // Le profil est déjà normalisé entre 0 et 1, pas besoin de le multiplier par la longueur
    // La longueur sera appliquée lors de la conversion 3D
    return profil.map(point => ({
      x: point.x, // Garde le profil normalisé
      y: point.y // Garde la forme verticale
    }));
  }

  /**
   * Convertit un profil 2D en points 3D et le pivote
   * Tâche 4.2: Duplication et rotation du profil
   * CORRECTION: Création d'une vraie géométrie 3D avec épaisseur uniforme
   * NOUVEAU: Support de l'aplatissement des baleines selon la hauteur de convergence
   */
  private convertirEtPivoterProfil(
    profil: Point2D[],
    pointAttache: Point3D,
    pointConvergence: Point3D,
    angle: number,
    epaisseur: number,
    parametres?: ParametresToit
  ): Point3D[] {
    console.log('🔧 convertirEtPivoterProfil - Épaisseur:', epaisseur, 'Profil points:', profil.length);
    
    const points3D: Point3D[] = [];
    
    // Calculer la direction de la baleine
    const direction = {
      x: pointConvergence.x - pointAttache.x,
      y: pointConvergence.y - pointAttache.y,
      z: pointConvergence.z - pointAttache.z
    };
    
    const longueur = Math.sqrt(direction.x ** 2 + direction.y ** 2 + direction.z ** 2);
    
    // Calculer la direction horizontale (sans composante Y) pour la courbure
    const directionHorizontale = {
      x: pointConvergence.x - pointAttache.x,
      y: 0,
      z: pointConvergence.z - pointAttache.z
    };
    
    const longueurTotale = Math.sqrt(directionHorizontale.x ** 2 + directionHorizontale.z ** 2);
    
    // Normaliser la direction horizontale
    if (longueurTotale > 0) {
      directionHorizontale.x /= longueurTotale;
      directionHorizontale.z /= longueurTotale;
    }
    
    // Normaliser la direction
    if (longueur > 0) {
      direction.x /= longueur;
      direction.y /= longueur;
      direction.z /= longueur;
    }
    
    // Calculer deux vecteurs perpendiculaires pour créer une section carrée/rectangulaire
    let perpendiculaire1 = { x: 0, y: 0, z: 0 };
    let perpendiculaire2 = { x: 0, y: 0, z: 0 };
    
    // Utiliser la direction horizontale pour les perpendiculaires
    // Choisir un vecteur de référence qui n'est pas parallèle à la direction horizontale
    if (Math.abs(directionHorizontale.z) < 0.9) {
      // Utiliser le vecteur Z comme référence
      perpendiculaire1.x = directionHorizontale.y * 1 - directionHorizontale.z * 0;
      perpendiculaire1.y = directionHorizontale.z * 0 - directionHorizontale.x * 1;
      perpendiculaire1.z = directionHorizontale.x * 0 - directionHorizontale.y * 0;
    } else {
      // Utiliser le vecteur X comme référence
      perpendiculaire1.x = directionHorizontale.y * 0 - directionHorizontale.z * 0;
      perpendiculaire1.y = directionHorizontale.z * 1 - directionHorizontale.x * 0;
      perpendiculaire1.z = directionHorizontale.x * 0 - directionHorizontale.y * 1;
    }
    
    // Normaliser perpendiculaire1
    const longueurPerp1 = Math.sqrt(perpendiculaire1.x ** 2 + perpendiculaire1.y ** 2 + perpendiculaire1.z ** 2);
    if (longueurPerp1 > 0) {
      perpendiculaire1.x /= longueurPerp1;
      perpendiculaire1.y /= longueurPerp1;
      perpendiculaire1.z /= longueurPerp1;
    }
    
    // Calculer perpendiculaire2 = directionHorizontale × perpendiculaire1
    perpendiculaire2.x = directionHorizontale.y * perpendiculaire1.z - directionHorizontale.z * perpendiculaire1.y;
    perpendiculaire2.y = directionHorizontale.z * perpendiculaire1.x - directionHorizontale.x * perpendiculaire1.z;
    perpendiculaire2.z = directionHorizontale.x * perpendiculaire1.y - directionHorizontale.y * perpendiculaire1.x;
    
    // Trouver la valeur Y maximale du profil pour normaliser la courbure
    const yMaxProfil = Math.max(...profil.map(p => p.y));
    
    // Créer une section rectangulaire pour chaque point du profil
    profil.forEach(point2D => {
      // Point central sur la ligne de la baleine
      // Utiliser la direction horizontale pour X et Z, et ajouter la hauteur le long de la baleine avec interpolation vers le point de convergence
      const t = point2D.x; // 0..1 le long de la baleine
      const yBase = pointAttache.y + (pointConvergence.y - pointAttache.y) * t;
      
      // Hauteur de courbure ABSOLUE contrôlée par le slider "hauteur du toit"
      // La hauteur maximale de la courbure est toujours à pointAttache.y + hauteurToit
      // peu importe la position du point de convergence
      const hauteurCourbure = parametres?.hauteurToit ?? 0;
      const courbureNormalisee = yMaxProfil > 0 ? (point2D.y / yMaxProfil) : 0;
      const flattenFactor = parametres?.aplatirBaleines ? 0.5 : 1.0; // aplatir légèrement si activé
      
      // Hauteur de référence = point d'attache + hauteur du slider
      const hauteurReference = pointAttache.y + hauteurCourbure;
      
      const pointCentral = {
        x: pointAttache.x + directionHorizontale.x * point2D.x * longueurTotale,
        y: yBase + (hauteurReference - yBase) * courbureNormalisee * flattenFactor,
        z: pointAttache.z + directionHorizontale.z * point2D.x * longueurTotale
      };
      
      const demiEpaisseur = epaisseur / 2;
      
      // Créer 4 points pour une section rectangulaire
      // Point 1: +perpendiculaire1, +perpendiculaire2
      points3D.push({
        x: pointCentral.x + perpendiculaire1.x * demiEpaisseur + perpendiculaire2.x * demiEpaisseur,
        y: pointCentral.y + perpendiculaire1.y * demiEpaisseur + perpendiculaire2.y * demiEpaisseur,
        z: pointCentral.z + perpendiculaire1.z * demiEpaisseur + perpendiculaire2.z * demiEpaisseur
      });
      
      // Point 2: -perpendiculaire1, +perpendiculaire2
      points3D.push({
        x: pointCentral.x - perpendiculaire1.x * demiEpaisseur + perpendiculaire2.x * demiEpaisseur,
        y: pointCentral.y - perpendiculaire1.y * demiEpaisseur + perpendiculaire2.y * demiEpaisseur,
        z: pointCentral.z - perpendiculaire1.z * demiEpaisseur + perpendiculaire2.z * demiEpaisseur
      });
      
      // Point 3: -perpendiculaire1, -perpendiculaire2
      points3D.push({
        x: pointCentral.x - perpendiculaire1.x * demiEpaisseur - perpendiculaire2.x * demiEpaisseur,
        y: pointCentral.y - perpendiculaire1.y * demiEpaisseur - perpendiculaire2.y * demiEpaisseur,
        z: pointCentral.z - perpendiculaire1.z * demiEpaisseur - perpendiculaire2.z * demiEpaisseur
      });
      
      // Point 4: +perpendiculaire1, -perpendiculaire2
      points3D.push({
        x: pointCentral.x + perpendiculaire1.x * demiEpaisseur - perpendiculaire2.x * demiEpaisseur,
        y: pointCentral.y + perpendiculaire1.y * demiEpaisseur - perpendiculaire2.y * demiEpaisseur,
        z: pointCentral.z + perpendiculaire1.z * demiEpaisseur - perpendiculaire2.z * demiEpaisseur
      });
    });
    
    console.log('🔧 convertirEtPivoterProfil - Points 3D générés:', points3D.length);
    return points3D;
  }

  /**
   * Génère les indices pour les triangles d'une baleine
   * CORRECTION: Adapté pour 4 points par section (géométrie rectangulaire)
   */
  private genererIndicesBaleine(nombrePointsProfil: number): number[] {
    const indices: number[] = [];
    
    for (let i = 0; i < nombrePointsProfil - 1; i++) {
      // Chaque section a 4 points (rectangle)
      const base = i * 4;
      const suivant = (i + 1) * 4;
      
      // Connecter les 4 faces du rectangle entre deux sections
      // Face 1: points 0,1 -> 4,5
      indices.push(base + 0, base + 1, suivant + 0);
      indices.push(base + 1, suivant + 1, suivant + 0);
      
      // Face 2: points 1,2 -> 5,6
      indices.push(base + 1, base + 2, suivant + 1);
      indices.push(base + 2, suivant + 2, suivant + 1);
      
      // Face 3: points 2,3 -> 6,7
      indices.push(base + 2, base + 3, suivant + 2);
      indices.push(base + 3, suivant + 3, suivant + 2);
      
      // Face 4: points 3,0 -> 7,4
       indices.push(base + 3, base + 0, suivant + 3);
       indices.push(base + 0, suivant + 0, suivant + 3);
    }
    
    return indices;
  }

  /**
   * Génère le point de jonction au sommet
   * Tâche 4.1: Point de Jonction
   */
  private genererPointJonction(
    pointConvergence: Point3D,
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): { type: TypePointJonction; geometrie: Point3D[]; indices: number[] } | undefined {
    // Générer point de jonction
    
    if (parametres.pointJonction === TypePointJonction.POINT_SIMPLE) {
      return undefined; // Pas de géométrie pour un point simple
    }
    
    const rayon = parametres.rayonPointJonction || 0.1;
    

    
    if (parametres.pointJonction === TypePointJonction.POUTRE_CIRCULAIRE) {
      // Génération poutre circulaire
      const resultat = this.genererPoutreCirculaireJonction(pointConvergence, rayon, donneesBase);
      return resultat;
    }
    
    // Type de jonction non reconnu
    return undefined;
  }



  /**
   * Génère une poutre circulaire de jonction EXACTEMENT comme les poutres de la structure de base
   */
  private genererPoutreCirculaireJonction(
    centre: Point3D,
    rayon: number,
    donneesBase: DonneesStructureBasePourToit
  ): { type: TypePointJonction; geometrie: Point3D[]; indices: number[] } {
    // Utiliser EXACTEMENT la même épaisseur que la structure de base (en mètres)
    const epaisseur = donneesBase.epaisseur ? donneesBase.epaisseur / 1000 : 0.02;
    const points: Point3D[] = [];
    const indices: number[] = [];
    
    // Déterminer le matériau pour choisir la forme
    const materiauStructure = this.obtenirMateriauStructureBase();
    
    // Construire une géométrie Three.js comme dans la base (segments tangents) et la fusionner
    const geometries: THREE.BufferGeometry[] = [];
    const segmentsCercle = 64; // plus de segments pour un anneau plus lisse
    const epaisseurMetres = epaisseur; // déjà en mètres ici

    // Créer des segments TANGENTS qui suivent le cercle (A->B)
    for (let i = 0; i < segmentsCercle; i++) {
      const angleA = (i / segmentsCercle) * 2 * Math.PI;
      const angleB = ((i + 1) / segmentsCercle) * 2 * Math.PI;

      const pointA = new THREE.Vector3(
        centre.x + rayon * Math.cos(angleA),
        centre.y,
        centre.z + rayon * Math.sin(angleA)
      );
      const pointB = new THREE.Vector3(
        centre.x + rayon * Math.cos(angleB),
        centre.y,
        centre.z + rayon * Math.sin(angleB)
      );

      // Direction tangentielle (A -> B)
      const direction = new THREE.Vector3().subVectors(pointB, pointA);
      const longueur = direction.length();
      if (longueur <= 0) continue;
      direction.normalize();

      // Créer la géométrie du segment EXACTEMENT comme dans la structure de base
      let geometrySegment: THREE.BufferGeometry;
      if (materiauStructure === 'tube_carre') {
        geometrySegment = new THREE.BoxGeometry(epaisseurMetres, longueur, epaisseurMetres);
      } else {
        geometrySegment = new THREE.CylinderGeometry(epaisseurMetres / 2, epaisseurMetres / 2, longueur, 12);
      }

      // Mesh temporaire pour appliquer rotation + position
      const tempMesh = new THREE.Mesh(geometrySegment);

      // Positionner au milieu du segment AB
      const centreSegment = new THREE.Vector3().addVectors(pointA, pointB).multiplyScalar(0.5);
      tempMesh.position.copy(centreSegment);

      // Orienter de l'axe Y vers la tangente (comme dans StructureBase)
      const axeY = new THREE.Vector3(0, 1, 0);
      const quaternion = new THREE.Quaternion().setFromUnitVectors(axeY, direction);
      tempMesh.setRotationFromQuaternion(quaternion);
      tempMesh.updateMatrix();

      // Appliquer la transformation à la géométrie pour fusion
      const g = geometrySegment.clone();
      g.applyMatrix4(tempMesh.matrix);
      geometries.push(g);
    }
    
    const merged = geometries.length > 1 ? BufferGeometryUtils.mergeGeometries(geometries) : (geometries[0] || new THREE.BufferGeometry());
    merged.computeVertexNormals();
    
    // Extraire vertices/indices pour la pipeline existante
    const positionAttr = merged.getAttribute('position') as THREE.BufferAttribute;
    const indexAttr = merged.getIndex();
    if (positionAttr) {
      for (let i = 0; i < positionAttr.count; i++) {
        points.push({ x: positionAttr.getX(i), y: positionAttr.getY(i), z: positionAttr.getZ(i) });
      }
    }
    if (indexAttr) {
      for (let i = 0; i < indexAttr.count; i++) {
        indices.push(indexAttr.getX(i));
      }
    }
    
    return {
      type: TypePointJonction.POUTRE_CIRCULAIRE,
      geometrie: points,
      indices
    };
  }

  /**
   * Crée un mesh Three.js pour une baleine en utilisant le profil 2D personnalisé
   */
  private creerMeshBaleine(baleine: GeometrieBaleine): THREE.Mesh | null {
    // Création mesh pour baleine
    
    // Utiliser les propriétés du matériau mis à jour
    // La couleur est déjà appliquée via this.materiau dans mettreAJourMateriau()
    
    // Vérifier le matériau de la structure de base pour décider du type de géométrie
    const materiauStructure = this.obtenirMateriauStructureBase();
    
    // Pour le tube carré, utiliser la géométrie simple (BoxGeometry)
    if (materiauStructure === 'tube_carre') {
      return this.creerMeshBaleineSimple(baleine);
    }
    
    // Utiliser la géométrie personnalisée basée sur le profil 2D pour les autres matériaux
    if (baleine.points3D.length === 0 || baleine.indices.length === 0) {
      // Pas de géométrie 3D disponible, utilisation géométrie simple
      return this.creerMeshBaleineSimple(baleine);
    }
    
    // Créer une géométrie personnalisée à partir des points 3D calculés
    const geometry = new THREE.BufferGeometry();
    
    // Convertir les points 3D en tableau de positions
    const positions = new Float32Array(baleine.points3D.length * 3);
    baleine.points3D.forEach((point, index) => {
      positions[index * 3] = point.x;
      positions[index * 3 + 1] = point.y;
      positions[index * 3 + 2] = point.z;
    });
    
    // Définir les attributs de la géométrie
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(baleine.indices);
    
    // Calculer les normales pour un éclairage correct
    geometry.computeVertexNormals();
    
    const mesh = new THREE.Mesh(geometry, this.materiau);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    // Mesh créé avec géométrie personnalisée
    return mesh;
  }
  
  /**
   * Crée un mesh simple en cas de problème avec la géométrie personnalisée
   */
  private creerMeshBaleineSimple(baleine: GeometrieBaleine): THREE.Mesh | null {
    // Calculer la direction et la longueur de la baleine
    const direction = {
      x: baleine.pointConvergence.x - baleine.pointAttache.x,
      y: baleine.pointConvergence.y - baleine.pointAttache.y,
      z: baleine.pointConvergence.z - baleine.pointAttache.z
    };
    
    const longueur = Math.sqrt(direction.x ** 2 + direction.y ** 2 + direction.z ** 2);
    
    // Normaliser la direction
    direction.x /= longueur;
    direction.y /= longueur;
    direction.z /= longueur;
    
    // Obtenir l'épaisseur depuis les paramètres enrichis de la structure
    // L'épaisseur est déjà en mètres (convertie dans ToitModule.tsx)
    const epaisseurMetres = this.derniereStructure?.parametres?.epaisseur || 0.02; // 2cm par défaut
    
    console.log('DEBUG BALEINE - Épaisseur utilisée:', epaisseurMetres, 'mètres');
    console.log('DEBUG BALEINE - Longueur baleine:', longueur, 'mètres');
    console.log('DEBUG BALEINE - Matériau structure:', this.obtenirMateriauStructureBase());
    
    // Créer la géométrie selon le matériau de la structure de base
    let geometry: THREE.BufferGeometry;
    const materiauStructure = this.obtenirMateriauStructureBase();
    
    console.log('DEBUG BALEINE - Matériau détecté:', materiauStructure);
    
    if (materiauStructure === 'tube_carre') {
      // Forme carrée pour tube carré
      geometry = new THREE.BoxGeometry(
        epaisseurMetres, // Largeur (épaisseur)
        epaisseurMetres, // Hauteur (épaisseur)
        epaisseurMetres // Profondeur (épaisseur) - tube carré uniforme
      );
      // Étirer la géométrie selon la longueur de la baleine
      geometry.scale(1, 1, longueur / epaisseurMetres);
      console.log('DEBUG BALEINE - Géométrie tube carré créée');
    } else {
      // Forme cylindrique pour fer à béton (par défaut)
      geometry = new THREE.CylinderGeometry(
        epaisseurMetres / 2, // Rayon haut
        epaisseurMetres / 2, // Rayon bas
        epaisseurMetres, // Hauteur (épaisseur) - cylindre uniforme
        8 // Segments radiaux
      );
      // Rotation pour aligner avec l'axe Z comme la BoxGeometry
      geometry.rotateX(Math.PI / 2);
      // Étirer la géométrie selon la longueur de la baleine
      geometry.scale(1, 1, longueur / epaisseurMetres);
      console.log('DEBUG BALEINE - Géométrie fer à béton créée');
    }
    
    const mesh = new THREE.Mesh(geometry, this.materiau);
    
    // Positionner au centre de la baleine
    const centre = {
      x: (baleine.pointAttache.x + baleine.pointConvergence.x) / 2,
      y: (baleine.pointAttache.y + baleine.pointConvergence.y) / 2,
      z: (baleine.pointAttache.z + baleine.pointConvergence.z) / 2
    };
    
    mesh.position.set(centre.x, centre.y, centre.z);
    
    // Orienter la baleine selon sa direction
    // La géométrie est maintenant orientée selon l'axe Z
    const axeZ = new THREE.Vector3(0, 0, 1);
    const directionVec = new THREE.Vector3(direction.x, direction.y, direction.z);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(axeZ, directionVec);
    mesh.setRotationFromQuaternion(quaternion);
    
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return mesh;
  }

  /**
   * Crée un mesh Three.js pour le point de jonction
   */
  private creerMeshJonction(
    jonction: { type: TypePointJonction; geometrie: Point3D[]; indices: number[] }
  ): THREE.Mesh | null {
    if (jonction.geometrie.length === 0 || jonction.indices.length === 0) {
      return null;
    }
    
    const geometry = new THREE.BufferGeometry();
    
    // Convertir les points en Float32Array
    const vertices = new Float32Array(jonction.geometrie.length * 3);
    jonction.geometrie.forEach((point, index) => {
      vertices[index * 3] = point.x;
      vertices[index * 3 + 1] = point.y;
      vertices[index * 3 + 2] = point.z;
    });
    
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(jonction.indices);
    geometry.computeVertexNormals();
    
    const mesh = new THREE.Mesh(geometry, this.materiauJonction);
    this.materiauJonction.side = THREE.DoubleSide;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    return mesh;
  }

  /**
   * Crée une structure vide
   */
  private creerStructureVide(parametres: ParametresToit): StructureToit {
    return {
      id: 'structure_toit_vide',
      parametres,
      baleines: [],
      pointConvergence: { x: 0, y: 0, z: 0 }
    };
  }

  /**
   * Met à jour le matériau selon la couleur
   */
  private mettreAJourMateriau(couleur: number): void {
    (this.materiau as THREE.MeshLambertMaterial).color.setHex(couleur);
    (this.materiauJonction as THREE.MeshLambertMaterial).color.setHex(couleur); // Même couleur que les baleines
  }

  /**
   * Met à jour la couleur des matériaux (méthode publique)
   */
  public mettreAJourCouleur(couleur: string): void {
    const couleurNumerique = typeof couleur === 'string' 
      ? parseInt(couleur.replace('#', ''), 16) 
      : parseInt(couleur as string, 16);
    
    this.mettreAJourMateriau(couleurNumerique);
  }

  /**
   * Force la mise à jour des matériaux et géométries existants
   */
  public forcerMiseAJourMateriau(couleur: string, materiau: string, epaisseur: number): void {
    const couleurNumerique = typeof couleur === 'string' 
      ? parseInt(couleur.replace('#', ''), 16) 
      : couleur;
    
    // Mettre à jour la couleur immédiatement
    this.mettreAJourMateriau(couleurNumerique);
    
    // Invalider complètement le cache pour forcer la régénération avec nouveaux paramètres
    this.derniereReference = null;
    this.derniereStructure = null;
    
    // Log pour confirmer la mise à jour forcée
    // Mise à jour forcée des matériaux
  }
  
  /**
   * Obtient le matériau de la structure de base
   */
  private obtenirMateriauStructureBase(): string {
    // Récupérer le matériau depuis les données de structure de base stockées
    if (this.derniereStructure?.parametres?.materiau) {
      const materiau = this.derniereStructure.parametres.materiau;
      
      // Le matériau peut être soit un enum TypeMateriauGlobal soit une chaîne
      // Conversion vers chaîne pour compatibilité
      if (materiau === 'tube_carre' || materiau === 'TUBE_CARRE') {
        return 'tube_carre';
      } else {
        return 'fer_a_beton';
      }
    }
    return 'fer_a_beton'; // Par défaut
  }

  /**
   * Calcule la distance entre deux points 3D
   */
  private calculerDistance3D(point1: Point3D, point2: Point3D): number {
    return Math.sqrt(
      (point2.x - point1.x) ** 2 +
      (point2.y - point1.y) ** 2 +
      (point2.z - point1.z) ** 2
    );
  }

  /**
   * Calcule l'angle d'un point par rapport au centre
   */
  private calculerAngle(point: Point3D, centre: Point3D): number {
    return Math.atan2(point.z - centre.z, point.x - centre.x);
  }





  /**
   * Vide le fichier de logs au démarrage
   */
  private viderFichierLogs(): void {
    try {
      console.log('🧹 Vidage du fichier de logs...');
      fetch('/api/clear-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        console.log('✅ Fichier de logs vidé avec succès');
        return response.json();
      })
      .then(data => {
        console.log('📝 Réponse serveur:', data);
      })
      .catch(error => {
        console.error('❌ Erreur lors du vidage des logs:', error);
      });
    } catch (error) {
      console.error('❌ Erreur dans viderFichierLogs:', error);
    }
  }

  public sauvegarderLogs(): void {
    const logsContent = this.logBuffer.join('\n');
    const blob = new Blob([logsContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Nettoie les ressources
   */
  public dispose(): void {
    this.derniereReference = null;
    this.derniereStructure = null;
    
    if (this.materiau) {
      this.materiau.dispose();
    }
    
    if (this.materiauJonction) {
      this.materiauJonction.dispose();
    }
  }
}
